// Foliage framing (golden_grass_mechs.png): dark teal shrubs hugging every rock, ruin end and
// the island rim, and stylized broadleaf trees (converted from the Godot project's
// Assets/nature/stylized_trees/broadleaf, see trees.json/bin) leaning out over the cliff edge
// and growing out of the big boulders.
//
// The collision rule holds: tree trunks stand either inside a rock's collision cylinder or
// outside the island edge (rooted in the hanging strata, where there is no ground to walk
// on). Shrubs are soft, walk-through foliage like the grass and never taller than ~1.3 m.
//
// Draw calls: one InstancedMesh per part (2 trees x trunk + canopy, 1 shrub) = 5.

import * as THREE from 'three';
import { World, NO_GROUND } from '../../../sim/world';
import { hash32, valueNoise } from '../../../sim/core';
import { paintedMaterial } from '../materials';
import { Jit } from './geo';
import { Clock, budget, sharedTexture } from '../warm';

interface PartRec { key: string; kind: 'trunk' | 'leaf'; nv: number; ni: number; i32: boolean; pos: number; uv: number; idx: number; bb: number[]; }
interface Spot { x: number; y: number; z: number; s: number; rot: number; tint: number; lean: number; }

const ASSET = '/assets/landscape/';

export class Foliage {
  readonly group = new THREE.Group();
  private disposables: { dispose(): void }[] = [];
  private q: number;
  /** true once trees.json/bin have loaded and the instanced meshes exist */
  ready = false;
  private gone = false;

  constructor(world: World, quality: number, leafTex: THREE.Texture, barkTex: THREE.Texture) {
    this.q = quality;
    this.group.name = 'foliage';
    const fl = new THREE.FileLoader();
    fl.setResponseType('json');
    fl.load(ASSET + 'trees.json', (json) => {
      const bl = new THREE.FileLoader();
      bl.setResponseType('arraybuffer');
      bl.load(ASSET + 'trees.bin', (bin) => budget.add(this.build(world, json as unknown as PartRec[], bin as ArrayBuffer, leafTex, barkTex)));
    });
  }

  /** In frame-budget slices (client/render/warm.ts): the trees land while the posse is fighting. */
  private *build(world: World, parts: PartRec[], bin: ArrayBuffer, leafTex: THREE.Texture, barkTex: THREE.Texture): Generator<unknown, void, unknown> {
    if (this.gone) return;
    const clk = new Clock(function* (at: string) { budget.note = at; yield; });
    const q = this.q;
    const geo = (p: PartRec, leaf: boolean) => {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(bin, p.pos, p.nv * 3), 3));
      g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(bin, p.uv, p.nv * 2), 2));
      g.setIndex(new THREE.BufferAttribute(p.i32 ? new Uint32Array(bin, p.idx, p.ni) : new Uint16Array(bin, p.idx, p.ni), 1));
      if (leaf) {
        // soft canopy light: normals radiate from the canopy's centre, so the cards shade as
        // one painted mass instead of flickering card by card
        g.computeBoundingBox();
        const c = g.boundingBox!.getCenter(new THREE.Vector3());
        c.y -= (g.boundingBox!.max.y - g.boundingBox!.min.y) * 0.15;
        const P = g.attributes.position, N = new Float32Array(p.nv * 3), v = new THREE.Vector3();
        for (let i = 0; i < p.nv; i++) {
          v.fromBufferAttribute(P, i).sub(c).normalize();
          v.y = v.y * 0.8 + 0.25; v.normalize();
          N[i * 3] = v.x; N[i * 3 + 1] = v.y; N[i * 3 + 2] = v.z;
        }
        g.setAttribute('normal', new THREE.BufferAttribute(N, 3));
      } else g.computeVertexNormals();
      g.computeBoundingSphere();
      this.disposables.push(g);
      return g;
    };
    const find = (key: string, kind: string) => parts.find((p) => p.key === key && p.kind === kind)!;

    const leafMat = paintedMaterial({ map: leafTex, alphaTest: 0.45, side: THREE.DoubleSide, vertexColors: false, color: 0xb8cfc0, wind: 0.05, nearFade: true });
    const shrubMat = paintedMaterial({ map: leafTex, alphaTest: 0.45, side: THREE.DoubleSide, vertexColors: false, color: 0xa9c3b4, wind: 0.09, nearFade: true });
    const barkMat = paintedMaterial({ map: barkTex, vertexColors: false, color: 0x8f7f72 });
    this.disposables.push(leafMat, shrubMat, barkMat);

    // ---------------------------------------------------------------- placement
    const trees: Spot[][] = [[], []];
    const shrubs: Spot[] = [];
    const j = new Jit(world.seed ^ 0xf011a6e);
    const nearTown = (x: number, z: number) => world.townMask(x, z) > 0.05;
    const nearGate = (x: number, z: number) => Math.hypot(x - world.gate.x, z - world.gate.z) < 12;
    const tint = () => j.next();

    for (const isl of world.islands) {
      yield* clk.tick('foliage:rim');
      // rim: trees rooted just outside the edge, shrubs along the lip
      const rimTrees = isl.main ? (q >= 3 ? 22 : q >= 2 ? 12 : q >= 1 ? 6 : 0) : (q >= 1 ? 3 : 0);
      for (let i = 0; i < rimTrees; i++) {
        // trees come in little groves, not a picket fence
      const grove = Math.floor(i / 3), gi = i % 3;
      const a = ((grove * 3) / rimTrees) * Math.PI * 2 + j.range(-0.05, 0.05) + gi * j.range(0.025, 0.05);
        const ux = Math.cos(a), uz = Math.sin(a);
        const er = world.edgeRadius(isl, ux, uz);
        const ix = isl.cx + ux * (er - 1.2), iz = isl.cz + uz * (er - 1.2);
        if (nearTown(ix, iz) || nearGate(ix, iz)) continue;
        const gy = world.terrain(ix, iz);
        if (gy === NO_GROUND) continue;
        const out = j.range(0.6, 2.2);
        const x = isl.cx + ux * (er + out), z = isl.cz + uz * (er + out);
        if (world.terrain(x, z) !== NO_GROUND) continue; // must be off the walkable ground
        const big = isl.main ? j.range(1.0, 1.7) : j.range(0.7, 1.1);
        trees[j.next() < 0.3 ? 0 : 1].push({ x, y: gy - j.range(1.6, 3.2), z, s: big, rot: j.range(0, 6.28), tint: tint(), lean: 0.12 });
      }
      const rimShrubs = isl.main ? [12, 8, 12, 36][q] : [3, 8, 12, 14][q];
      for (let i = 0; i < rimShrubs; i++) {
        const a = j.range(0, Math.PI * 2);
        const ux = Math.cos(a), uz = Math.sin(a);
        const er = world.edgeRadius(isl, ux, uz);
        const d = er - j.range(-0.4, 2.2);
        const x = isl.cx + ux * d, z = isl.cz + uz * d;
        if (nearTown(x, z) || nearGate(x, z)) continue;
        let gy = world.terrain(x, z);
        if (gy === NO_GROUND) gy = world.terrain(isl.cx + ux * (er - 0.6), isl.cz + uz * (er - 0.6));
        if (gy === NO_GROUND) continue;
        shrubs.push({ x, y: gy - 0.25, z, s: j.range(0.8, 1.5), rot: j.range(0, 6.28), tint: tint(), lean: 0 });
      }
    }

    // boulders: shrubs around the foot, the odd tree growing out of the big ones
    for (const c of world.cyls) {
      if (c.kind !== 'rock' && c.kind !== 'pillar') continue;
      const h = hash32(c.variant, 0xb05, Math.round(c.z * 13));
      // the big meadow boulders (sim variants 46+) are framing masses: a dark skirt of shrubs
      const framer = c.kind === 'rock' && c.variant >= 46 && c.variant <= 48 && c.r > 3;
      const nB = framer ? 4 : q <= 3 ? (h & 1) : 2;
      for (let k = 0; k < nB; k++) {
        const a = framer ? (k / nB) * Math.PI * 2 + j.range(-0.5, 0.5) : j.range(0, Math.PI * 2), d = c.r * j.range(0.8, 1.2);
        const x = c.x + Math.cos(a) * d, z = c.z + Math.sin(a) * d;
        const gy = world.terrain(x, z);
        if (gy === NO_GROUND) continue;
        shrubs.push({ x, y: gy - 0.2, z, s: framer ? j.range(1.3, 1.9) : j.range(0.7, 1.3) * Math.min(1.3, 0.6 + c.r * 0.35), rot: j.range(0, 6.28), tint: framer ? 0 : tint(), lean: 0 });
      }
      if (q >= 1 && c.kind === 'rock' && c.r > (q >= 2 ? 1.45 : 1.7) && (h >>> 8) % 3 === 0) {
        // trunk stands inside the rock's cylinder (0.55 r from the centre), base inside the stone
        const a = j.range(0, Math.PI * 2);
        const x = c.x + Math.cos(a) * c.r * 0.55, z = c.z + Math.sin(a) * c.r * 0.55;
        trees[(h >>> 12) % 4 === 0 ? 0 : 1].push({ x, y: c.y0 + (c.y1 - c.y0) * 0.35, z, s: j.range(0.55, 0.85), rot: j.range(0, 6.28), tint: tint(), lean: 0.05 });
      }
    }
    // ruin ends
    for (const b of world.boxes) {
      if (b.kind !== 'ruin') continue;
      const alongX = b.hx > b.hz;
      for (const sgn of [-1, 1]) {
        if (q <= 0 && sgn > 0) continue;
        const x = b.x + (alongX ? sgn * (b.hx + 0.3) : j.range(-0.6, 0.6));
        const z = b.z + (alongX ? j.range(-0.6, 0.6) : sgn * (b.hz + 0.3));
        const gy = world.terrain(x, z);
        if (gy === NO_GROUND) continue;
        shrubs.push({ x, y: gy - 0.2, z, s: j.range(0.8, 1.3), rot: j.range(0, 6.28), tint: tint(), lean: 0 });
      }
    }
    // a few lone clumps out in the meadow (away from the path, the arena floor and the town)
    const lone = [6, 4, 8, 18][q];
    for (let i = 0; i < lone * 3 && shrubs.length < 2000; i++) {
      const isl = world.islands[0];
      const a = j.range(0, Math.PI * 2), r = j.range(18, isl.radius * 0.85);
      const x = Math.cos(a) * r, z = Math.sin(a) * r;
      if (nearTown(x, z) || nearGate(x, z)) continue;
      if (valueNoise(world.seed ^ 0x10e, x / 20, z / 20) < 0.55) continue;
      const gy = world.terrain(x, z);
      if (gy === NO_GROUND) continue;
      let blocked = false;
      for (const p of world.path) if (Math.abs(p.x - x) < 4 && Math.abs(p.z - z) < 4) { blocked = true; break; }
      if (blocked) continue;
      shrubs.push({ x, y: gy - 0.2, z, s: j.range(0.6, 1.6), rot: j.range(0, 6.28), tint: tint(), lean: 0 });
      // a smaller companion so clumps read as thickets
      shrubs.push({ x: x + j.range(-1.2, 1.2), y: gy - 0.25, z: z + j.range(-1.2, 1.2), s: j.range(0.5, 0.9), rot: j.range(0, 6.28), tint: tint(), lean: 0 });
    }

    // ferns: the dark teal foreground of the ref -- clustered along the path verges, at the
    // feet of rocks and along the rim, so the camera always looks over a dark band of leaves
    const ferns: Spot[] = [];
    const fernOk = (x: number, z: number) => {
      if (nearTown(x, z) || nearGate(x, z)) return NO_GROUND;
      for (const p of world.path) if (Math.abs(p.x - x) < 2.3 && Math.abs(p.z - z) < 2.3) return NO_GROUND;
      return world.terrain(x, z);
    };
    const fernAt = (x: number, z: number, s: number) => {
      const gy = fernOk(x, z);
      if (gy !== NO_GROUND) ferns.push({ x, y: gy - 0.05, z, s, rot: j.range(0, 6.28), tint: j.next(), lean: 0.1 });
    };
    // path verges: big dark clumps (a shrub or two wrapped in a skirt of ferns) every ~10-14 m,
    // alternating sides, so the gameplay camera nearly always has a dark mass at a frame edge
    const clump = (x: number, z: number, big: number) => {
      const gy = fernOk(x, z);
      if (gy === NO_GROUND) return;
      const nS = 1;
      for (let k = 0; k < nS; k++) {
        const sx = x + j.range(-0.8, 0.8), sz = z + j.range(-0.8, 0.8);
        const sy = fernOk(sx, sz);
        if (sy !== NO_GROUND) shrubs.push({ x: sx, y: sy - 0.2, z: sz, s: j.range(1.0, 1.6) * (k ? 0.75 : 1), rot: j.range(0, 6.28), tint: j.next() * 0.5, lean: 0 });
      }
      const nF = [1, 2, 2, 3][q];
      for (let k = 0; k < nF; k++) {
        const a = (k / nF) * Math.PI * 2 + j.range(-0.3, 0.3), d = j.range(0.9, 2.2);
        fernAt(x + Math.cos(a) * d, z + Math.sin(a) * d, j.range(0.9, 1.5));
      }
    };
    let side = 1, acc = 0;
    for (let i = 1; i < world.path.length - 1; i++) {
      const a = world.path[i - 1], b = world.path[i + 1], p = world.path[i];
      acc += Math.hypot(p.x - a.x, p.z - a.z);
      if (acc < (q <= 0 ? 24 : q === 1 ? 19 : 11)) continue;
      acc = j.range(-3, 0);
      const dx = b.x - a.x, dz = b.z - a.z, l = Math.hypot(dx, dz) || 1;
      side = j.next() < 0.75 ? -side : side;
      const d = j.range(3.6, 6.5);
      clump(p.x - (dz / l) * d * side, p.z + (dx / l) * d * side, j.next());
    }
    // rim: clumps along the lip
    for (const isl of world.islands) {
      if (!isl.main) continue;
      const n = [6, 12, 16, 32][q];
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + j.range(-0.1, 0.1), ux = Math.cos(a), uz = Math.sin(a);
        const d = world.edgeRadius(isl, ux, uz) - j.range(2.5, 4.5);
        clump(isl.cx + ux * d, isl.cz + uz * d, j.next());
      }
    }
    for (const c of world.cyls) {
      if (c.kind !== 'rock' && c.kind !== 'pillar') continue;
      const n = [1, 1, 1, 2][q];
      for (let k = 0; k < n; k++) {
        const a = j.range(0, Math.PI * 2), d = c.r + j.range(0.2, 1.1);
        fernAt(c.x + Math.cos(a) * d, c.z + Math.sin(a) * d, j.range(1.0, 1.6));
      }
    }
    for (const isl of world.islands) {
      const n = isl.main ? [4, 10, 12, 20][q] : [2, 4, 5, 6][q];
      for (let i = 0; i < n; i++) {
        const a = j.range(0, Math.PI * 2), ux = Math.cos(a), uz = Math.sin(a);
        const d = world.edgeRadius(isl, ux, uz) - j.range(0.8, 3.5);
        fernAt(isl.cx + ux * d, isl.cz + uz * d, j.range(1.1, 1.8));
      }
    }

    // nothing grows in the water, on the stream banks or inside a spire or an arch's leg
    const solid = world.cyls.filter((c) => c.kind === 'spire' || c.kind === 'archleg');
    const dry = (sp: Spot) => world.waterAt(sp.x, sp.z) === NO_GROUND && world.dryAt(sp.x, sp.z, -2.6) &&
      !solid.some((c) => Math.hypot(sp.x - c.x, sp.z - c.z) < c.r + 0.4);
    for (const list of [shrubs, ferns, trees[0], trees[1]]) {
      yield* clk.tick('foliage:dry');
      let n = 0;
      for (const sp of list) if (dry(sp)) list[n++] = sp;
      list.length = n;
    }

    // ---------------------------------------------------------------- instancing
    const treeKeys = ['tree_a', 'tree_b'];
    const deep = new THREE.Color(0x5e9c96), olive = new THREE.Color(0x9fb88a), warm = new THREE.Color(0xd8b878);
    const tcol = new THREE.Color();
    const m = new THREE.Matrix4(), qt = new THREE.Quaternion(), e = new THREE.Euler(), sv = new THREE.Vector3(), pv = new THREE.Vector3();
    const place = (mesh: THREE.InstancedMesh, list: Spot[], scale: number, colorize: boolean) => {
      list.forEach((sp, i) => {
        e.set(sp.lean * Math.cos(sp.rot * 3), sp.rot, sp.lean * Math.sin(sp.rot * 2));
        qt.setFromEuler(e);
        sv.setScalar(sp.s * scale);
        m.compose(pv.set(sp.x, sp.y, sp.z), qt, sv);
        mesh.setMatrixAt(i, m);
        if (colorize) {
          tcol.copy(deep).lerp(olive, sp.tint * 0.7);
          if (sp.tint > 0.85) tcol.lerp(warm, 0.35);
          mesh.setColorAt(i, tcol);
        }
      });
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      mesh.computeBoundingSphere();
    };
    for (let ti = 0; ti < treeKeys.length; ti++) {
      const key = treeKeys[ti], list = trees[ti];
      if (!list.length) continue;
      yield* clk.tick('foliage:trees');
      const lp = find(key, 'leaf'), tp = find(key, 'trunk');
      const H = lp.bb[4];
      const scale = 9 / H; // ~9 m tall at s = 1
      const trunk = new THREE.InstancedMesh(geo(tp, false), barkMat, list.length);
      yield* clk.tick('foliage:crown');
      const crown = new THREE.InstancedMesh(geo(lp, true), leafMat, list.length);
      place(trunk, list, scale, false);
      place(crown, list, scale, true);
      trunk.castShadow = false;
      crown.castShadow = q >= 3;
      trunk.receiveShadow = crown.receiveShadow = true;
      trunk.name = 'treeTrunk' + ti; crown.name = 'treeCrown' + ti;
      this.group.add(trunk, crown);
    }
    yield* clk.tick('foliage:shrubs');
    if (shrubs.length) {
      const sp = find('bush', 'leaf');
      const scale = 1.3 / sp.bb[4]; // ~1.3 m tall at s = 1
      const bg = geo(sp, true);
      // below q3 draw ~65% of the bush's leaf cards: same mass, two thirds of the triangles
      if (q < 3 && bg.index) bg.setDrawRange(0, Math.floor(bg.index.count * 0.65 / 3) * 3);
      const bush = new THREE.InstancedMesh(bg, shrubMat, shrubs.length);
      place(bush, shrubs, scale, true);
      bush.castShadow = false;
      bush.receiveShadow = true;
      bush.name = 'shrubs';
      this.group.add(bush);
    }
    yield* clk.tick('foliage:ferns');
    if (ferns.length) {
      const fernTex = sharedTexture(ASSET + 'fern.png', 'srgb', (t) => { t.colorSpace = THREE.SRGBColorSpace; });
      const fernMat = paintedMaterial({ map: fernTex, alphaTest: 0.5, side: THREE.DoubleSide, vertexColors: false, color: 0xffffff, wind: 0.12 });
      const fg = fernGeometry(q >= 3 ? 8 : 7, q >= 1 ? 4 : 3);
      this.disposables.push(fernTex, fernMat, fg);
      const fern = new THREE.InstancedMesh(fg, fernMat, ferns.length);
      const fDeep = new THREE.Color(0x2c5a55), fOlive = new THREE.Color(0x4f6e4a);
      ferns.forEach((sp, i) => {
        e.set(0, sp.rot, 0);
        qt.setFromEuler(e);
        m.compose(pv.set(sp.x, sp.y, sp.z), qt, sv.setScalar(sp.s));
        fern.setMatrixAt(i, m);
        fern.setColorAt(i, tcol.copy(fDeep).lerp(fOlive, sp.tint * 0.6));
      });
      fern.instanceMatrix.needsUpdate = true;
      fern.instanceColor!.needsUpdate = true;
      fern.computeBoundingSphere();
      fern.receiveShadow = true;
      fern.name = 'ferns';
      this.group.add(fern);
    }
    this.ready = true;
  }

  dispose() {
    this.gone = true;
    for (const d of this.disposables) d.dispose();
  }
}

/** Soft fern: an outer ring of long fronds that arch out and droop at the tips, and an inner
 *  tier of shorter, more upright ones -- a layered mound, not a star. */
export function fernGeometry(fronds: number, seg: number) {
  const pos: number[] = [], nrm: number[] = [], uv: number[] = [], idx: number[] = [];
  const j = new Jit(0xfe12);
  const inner = Math.max(3, Math.round(fronds * 0.5));
  const total = fronds + inner;
  for (let f = 0; f < total; f++) {
    const outer = f < fronds;
    const a = outer ? (f / fronds) * Math.PI * 2 + j.range(-0.2, 0.2) : ((f - fronds) / inner) * Math.PI * 2 + 0.6 + j.range(-0.3, 0.3);
    const len = outer ? j.range(0.95, 1.25) : j.range(0.55, 0.75);
    const w = (outer ? 0.3 : 0.26) * len;
    const up0 = outer ? j.range(0.55, 0.8) : j.range(0.95, 1.2);
    const droop = outer ? j.range(1.3, 1.7) : j.range(0.8, 1.1); // total bend down along the frond
    const roll = j.range(-0.35, 0.35); // frond surface tilted about its spine
    const ca = Math.cos(a), sa = Math.sin(a);
    const base = pos.length / 3;
    let x = 0, y = outer ? 0.04 : 0.1, el = up0;
    for (let k = 0; k <= seg; k++) {
      const t = k / seg;
      const ww = w * Math.sin(Math.min(1, 0.25 + t * 1.1) * Math.PI * 0.62) * (1 - 0.7 * t * t);
      const sx = -sa * ww * Math.cos(roll), sz = ca * ww * Math.cos(roll), sy = ww * Math.sin(roll);
      // normals mostly up and a little out: the fern shades as a soft mound
      const nx = ca * 0.35, nz = sa * 0.35, ny = 0.94;
      pos.push(ca * x - sx, y - sy, sa * x - sz, ca * x + sx, y + sy, sa * x + sz);
      nrm.push(nx, ny, nz, nx, ny, nz);
      uv.push(0, t, 1, t);
      const stp = len / seg;
      x += Math.cos(el) * stp; y += Math.sin(el) * stp;
      el -= droop / seg * (0.6 + 0.8 * t);
    }
    for (let k = 0; k < seg; k++) {
      const r0 = base + k * 2, r1 = r0 + 2;
      idx.push(r0, r1, r0 + 1, r0 + 1, r1, r1 + 1);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  return g;
}
